import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Camera, Clock, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Package {
  id: string;
  name: string;
  price: number;
  features: string[];
  is_featured: boolean;
  description: string | null;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  description: string | null;
}

interface MediaImage {
  url: string;
  alt: string;
}

const Photography = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroImages, setHeroImages] = useState<MediaImage[]>([]);
  const [mediaLoading, setMediaLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [packagesRes, addonsRes] = await Promise.all([
        supabase
          .from("pricing_packages")
          .select("*")
          .eq("is_active", true)
          .eq("category", "photography")
          .order("sort_order"),
        supabase
          .from("pricing_addons")
          .select("*")
          .eq("is_active", true)
          .eq("category", "photography")
          .order("sort_order"),
      ]);

      if (packagesRes.data) {
        const sorted = [...packagesRes.data].sort((a, b) => b.price - a.price);
        const featuredIdx = sorted.findIndex(p => p.is_featured);
        if (featuredIdx !== -1 && featuredIdx !== 1 && sorted.length > 1) {
          const [featured] = sorted.splice(featuredIdx, 1);
          sorted.splice(1, 0, featured);
        }
        setPackages(sorted);
      }
      if (addonsRes.data) setAddons(addonsRes.data);

      try {
        const { data: locations } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "photography_page");

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          const { data: mediaData } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(2);

          if (mediaData && mediaData.length > 0) {
            setHeroImages(
              mediaData.map((m) => ({
                url: m.url,
                alt: m.alt_text || m.title,
              }))
            );
          }
        }
      } catch (error) {
        console.error("Error fetching portfolio media:", error);
      }

      setLoading(false);
      setMediaLoading(false);
    };
    fetchData();
  }, []);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(price);

  const valueProps = [
    {
      icon: Heart,
      title: "Documentary Style",
      description:
        "We capture real moments as they unfold, creating authentic images that tell your story.",
    },
    {
      icon: Camera,
      title: "Planning Support",
      description:
        "We help create your wedding day timeline to ensure perfect lighting and stress-free coverage.",
    },
    {
      icon: Clock,
      title: "Fast Delivery",
      description:
        "Sneak peeks within 48 hours, full gallery delivered in 4-6 weeks guaranteed.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Affordable Wedding Photography | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Timeless wedding photography that captures every precious moment. Packages starting at $995. Serving New Jersey, Florida & Colorado."
        />
        <meta property="og:title" content="Affordable Wedding Photography | Pixie Clicks" />
        <meta property="og:description" content="Timeless wedding photography without the luxury price tag. Packages starting at $995." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/photography-by-pixie-clicks" />
        <link rel="canonical" href="https://pixieclicks.com/photography-by-pixie-clicks" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Wedding Photography",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            },
            "description": "Affordable wedding photography services capturing every precious moment.",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "USD",
                "price": "995",
                "minPrice": "995"
              }
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32 bg-muted/30">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h1 className="font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
                  Timeless Wedding <span className="text-primary">Photography</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  We capture the moments that matter most — the stolen glances,
                  the happy tears, the spontaneous laughter. Our documentary
                  approach ensures nothing is missed while our artistic eye
                  creates stunning portraits you'll treasure forever.
                </p>
                <Button variant="hero" size="lg" className="mt-8" asChild>
                  <Link to="/contact">Check Availability</Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="grid gap-4 grid-cols-2"
              >
                {mediaLoading ? (
                  <>
                    <Skeleton className="aspect-[3/4] rounded-xl" />
                    <Skeleton className="aspect-[3/4] rounded-xl mt-8" />
                  </>
                ) : heroImages.length > 0 ? (
                  <>
                    <img
                      src={heroImages[0].url}
                      alt={heroImages[0].alt}
                      className="rounded-xl shadow-card"
                    />
                    {heroImages[1] && (
                      <img
                        src={heroImages[1].url}
                        alt={heroImages[1].alt}
                        className="rounded-xl shadow-card mt-8"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <div className="aspect-[3/4] rounded-xl bg-gradient-to-br from-primary/20 to-muted flex items-center justify-center">
                      <Camera className="h-16 w-16 text-primary/30" />
                    </div>
                    <div className="aspect-[3/4] rounded-xl bg-gradient-to-br from-muted to-primary/10 mt-8 flex items-center justify-center">
                      <Heart className="h-16 w-16 text-primary/30" />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="section-padding">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Why Choose Our Photography
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {valueProps.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section-padding bg-muted/30">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Photography Packages
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
              Starting at just $995 — choose the package that fits your vision
              and budget
            </p>

            {loading ? (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative flex flex-col rounded-xl border p-6 ${
                      pkg.is_featured
                        ? "border-primary bg-primary/5 shadow-rose"
                        : "border-border bg-card"
                    }`}
                  >
                    {pkg.is_featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                        Most Popular
                      </span>
                    )}
                    <h3 className="font-serif text-xl font-semibold text-foreground">
                      {pkg.name.replace("Photography ", "")}
                    </h3>
                    <p className="mt-2 text-3xl font-bold text-primary">
                      {formatPrice(pkg.price)}
                    </p>
                    {pkg.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {pkg.description}
                      </p>
                    )}
                    <ul className="mt-6 flex-1 space-y-2">
                      {pkg.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-sm"
                        >
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={pkg.is_featured ? "hero" : "outline"}
                      className="mt-6 w-full"
                      asChild
                    >
                      <Link to={`/contact?package=${encodeURIComponent(pkg.name)}`}>Check Availability</Link>
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Add-ons */}
        {addons.length > 0 && (
          <section className="section-padding">
            <div className="container-wide">
              <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
                Make It Yours
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
                Customize your experience with these handpicked favorites our
                couples love most
              </p>
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {addons.map((addon) => (
                  <div
                    key={addon.id}
                    className="flex flex-col rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-medium text-foreground">
                        {addon.name}
                      </h3>
                      <p className="text-xl font-bold text-primary">
                        {formatPrice(addon.price)}
                      </p>
                    </div>
                    {addon.description && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {addon.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Let's Talk About Your Wedding Day
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              No pressure. Just real answers, friendly guidance, and expert
              advice to help you plan the photography you deserve.
            </p>
            <Button variant="hero" size="lg" className="mt-8" asChild>
              <Link to="/contact">Let's Chat About Your Wedding</Link>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Photography;
